/**
 * Utility helper to handle exports of Users data to Excel (XLS) and CSV/TXT.
 */

import configCountries from "../../../Config/countries.json";

// Helper to look up country names
const getCountryName = (countryCode) => {
    if (!countryCode) return "";
    const country = configCountries.countries.find(
        (c) => c.value.toLowerCase() === countryCode.toLowerCase()
    );
    return country ? country.label : countryCode;
};

// Helper to get formatted nested values from user object
export const getCellValue = (item, key, t) => {
    switch (key) {
        case "Name":
            return item.Name || "";
        case "Username":
            return item.Username || "";
        case "Email":
            return item.Email || "";
        case "Phone":
            return item.Phone || "";
        case "UserType":
            return item.UserType ? item.UserType.Name : "";
        case "School":
            return item.School ? item.School.Name : "";
        case "Country":
            return getCountryName(item.Country);
        case "LastLogin":
            return item.LastLogin || "";
        case "ActivateUntilDate":
            return item.ActivateUntilDate === "0" ? t("Pages.UsersControl.NotActive") : item.ActivateUntilDate || "";
        case "Status":
            if (item.IsLocked) return t("Pages.UsersControl.Locked");
            return item.IsActive ? t("Pages.UsersControl.Active") : t("Pages.UsersControl.NotActive");
        case "CreationDate":
            return item.CreationDate || "";
        default:
            return "";
    }
};

/**
 * Export data to Excel (XLS) via standard HTML Table format.
 */
export const exportToXLS = (data, selectedColumns, filename = "users_export", t) => {
    let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" \n`;
    html += `xmlns:x="urn:schemas-microsoft-com:office:excel" \n`;
    html += `xmlns="http://www.w3.org/TR/REC-html40" dir="rtl">\n`;
    html += `<head>\n`;
    html += `  <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8" />\n`;
    html += `  <style>\n`;
    html += `    table { border-collapse: collapse; direction: rtl; }\n`;
    html += `    th { background-color: #f1f5f9; color: #1e293b; font-weight: bold; border: 1px solid #cbd5e1; padding: 8px; text-align: right; }\n`;
    html += `    td { border: 1px solid #cbd5e1; padding: 8px; text-align: right; mso-number-format:"\\@"; }\n`;
    html += `  </style>\n`;
    html += `</head>\n`;
    html += `<body dir="rtl">\n`;
    html += `  <table>\n`;
    html += `    <thead>\n`;
    html += `      <tr>\n`;
    selectedColumns.forEach(col => {
        html += `        <th>${col.label}</th>\n`;
    });
    html += `      </tr>\n`;
    html += `    </thead>\n`;
    html += `    <tbody>\n`;
    
    data.forEach(item => {
        html += `      <tr>\n`;
        selectedColumns.forEach(col => {
            const val = getCellValue(item, col.key, t);
            const cleanVal = String(val !== null && val !== undefined ? val : "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
            html += `        <td>${cleanVal}</td>\n`;
        });
        html += `      </tr>\n`;
    });
    
    html += `    </tbody>\n`;
    html += `  </table>\n`;
    html += `</body>\n`;
    html += `</html>`;

    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel;charset=utf-8" });
    triggerDownload(blob, `${filename}.xls`);
};

/**
 * Export data to CSV/TXT with standard comma-separation and UTF-8 BOM.
 */
export const exportToCSV = (data, selectedColumns, filename = "users_export", t) => {
    const BOM = "\ufeff";
    const headers = selectedColumns.map(col => `"${col.label.replace(/"/g, '""')}"`).join(",");
    const rows = data.map(item => {
        return selectedColumns.map(col => {
            const val = getCellValue(item, col.key, t);
            return `"${String(val).replace(/"/g, '""').replace(/[\n\r]/g, " ")}"`;
        }).join(",");
    }).join("\n");

    const fileContent = BOM + headers + "\n" + rows;
    const blob = new Blob([fileContent], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${filename}.csv`);
};

/**
 * Trigger browser file download
 */
const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
